import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { cashMovementApi } from '@/services/api';
import type { CashMovement } from '@/types';
import { X, Loader2 } from 'lucide-react';
import { formatMoney } from '@/utils/currency';

interface Props {
  open: boolean;
  onClose: () => void;
  shiftName: string;
}

export default function CashMovementsListModal({ open, onClose, shiftName }: Props) {
  const { t } = useTranslation();

  const { data: movements = [], isLoading } = useQuery({
    queryKey: ['cashMovements', shiftName],
    queryFn: async () => {
      const res = await cashMovementApi.listForShift(shiftName);
      return res.data.message as CashMovement[];
    },
    enabled: open && !!shiftName,
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-fade-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <h3 className="font-semibold text-gray-950 text-lg">Today's Cash Movements</h3>
            <p className="text-xs text-gray-500">All cash movements recorded during this shift</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[400px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-gray-500 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading movements...
            </div>
          ) : movements.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500 text-sm">No cash movements recorded in this shift.</p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <th className="pb-3 px-2">Type</th>
                  <th className="pb-3 px-2">Reason</th>
                  <th className="pb-3 px-2">Remarks</th>
                  <th className="pb-3 px-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {movements.map((mov) => {
                  const isAddition = mov.movement_type === 'Paid In';
                  return (
                    <tr key={mov.name} className="hover:bg-gray-50/50">
                      <td className="py-3 px-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                          isAddition 
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-100' 
                            : 'bg-orange-50 text-orange-800 border-orange-100'
                        }`}>
                          {mov.movement_type}
                        </span>
                      </td>
                      <td className="py-3 px-2 font-medium text-gray-900">
                        {mov.reason || '-'}
                      </td>
                      <td className="py-3 px-2 text-gray-500 max-w-[150px] truncate" title={mov.remarks}>
                        {mov.remarks || '-'}
                      </td>
                      <td className={`py-3 px-2 font-semibold font-mono text-right ${
                        isAddition ? 'text-emerald-700' : 'text-orange-700'
                      }`}>
                        {isAddition ? '+' : '-'}{formatMoney(mov.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        
        <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
